import styled from 'styled-components';
import { TabList, Tab, Container, StyledSpinner, darkColors } from '@tryhackme/thm-ui-components';
import { Link } from 'react-router-dom';

import { ReactComponent as BannerSVG } from 'src/images/svgs/hacktivities/hacktivities-banner.svg';

export const StyledBanner = styled.section`
  padding: 3.2rem 0;
  min-height: 26.3rem;
  height: auto;

  h1 {
    color: ${({ theme }) => theme.colors.grey[100]};
    margin-bottom: 1rem;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  &[data-type='learn'] {
    background: rgb(8, 96, 138);
  }

  &[data-type='practice'] {
    background: rgb(189, 88, 0);
  }

  &[data-type='search'] {
    background: ${({ theme }) => theme.colors.secondary.lighter};
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    padding: 1.8rem 0;
    height: 28.2rem;

    &[data-type='learn'] {
      background: linear-gradient(
        105deg,
        rgb(8, 96, 138) 0%,
        rgb(8, 96, 138) 60%,
        ${({ theme }) => theme.colors.secondary.main} 60%
      );
    }

    &[data-type='practice'] {
      background: linear-gradient(
        105deg,
        rgb(189, 88, 0) 0%,
        rgb(189, 88, 0) 60%,
        ${({ theme }) => theme.colors.secondary.main} 60%
      );
    }

    &[data-type='search'] {
      background: linear-gradient(
        105deg,
        ${({ theme }) => theme.colors.secondary.lighter} 0%,
        ${({ theme }) => theme.colors.secondary.lighter} 60%,
        ${({ theme }) => theme.colors.black} 60%
      );
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.sm}) {
    height: auto;
    min-height: 310px;
  }
`;

export const StyledTabList = styled(TabList)<{ isDarkMode?: boolean }>`
  text-align: center;
  background-color: ${({ theme, isDarkMode }) =>
    isDarkMode ? theme.colors.secondary.main : theme.colors.secondary.light};
  position: relative;
  box-shadow: 0px -6px 20px 0px rgba(0, 0, 0, 0.2);
  button {
    color: ${({ theme }) => theme.colors.grey[100]};

    &:hover {
      background-color: ${({ theme }) => theme.colors.grey[900]};
    }

    &[data-state='active'] {
      color: ${({ theme }) => theme.colors.grey[100]};
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    text-align: left;
  }
`;

export const StyledMainTab = styled(Tab)`
  &[data-tab='learn']:before {
      background-color: ${darkColors.info.main};
    }
  }
  &[data-tab='search']:before {
      background-color: ${darkColors.purple.main};
    }
  }
`;

export const StyledContent = styled.div`
  flex-basis: 100%;
  color: ${({ theme }) => theme.colors.grey[100]};
  p {
    font-size: 1.8rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    flex-basis: 60%;
  }
`;

export const StyledContentStatsWrapper = styled.div`
  display: flex;
  gap: 4.8rem;
  margin-top: 3.6rem;
`;

export const StyledImageWrapper = styled.div`
  flex-basis: 30%;
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;

  p {
    color: ${({ theme }) => theme.colors.grey[100]};
    position: absolute;
    font-size: 2.4rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.xs}) {
    display: none;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    display: flex;
  }
`;

export const StyledBannerSvg = styled(BannerSVG)`
  &[data-type='learn'],
  &[data-type='practice'] {
    .search-icon {
      display: none;
    }
  }

  &[data-type='practice'],
  &[data-type='search'] {
    .book-icon {
      fill: ${({ theme }) => theme.colors.grey[400]};
    }
    .circle-top {
      fill: ${({ theme }) => theme.colors.secondary.lighter};
    }
  }

  &[data-type='learn'],
  &[data-type='search'] {
    .circle-bottom {
      fill: ${({ theme }) => theme.colors.secondary.lighter};
    }
  }

  &[data-type='learn'] {
    .circle-top {
      fill: rgb(8, 96, 138);
    }

    .book-icon {
      fill: ${({ theme }) => theme.colors.grey[100]};
    }
    .practice-icon {
      fill: ${({ theme }) => theme.colors.grey[400]};
    }
  }

  &[data-type='practice'] {
    .circle-bottom {
      fill: rgb(189, 88, 0);
    }

    .practice-icon {
      fill: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-type='search'] {
    .practice-icon {
      display: none;
    }

    .search-icon {
      display: block;
      fill: ${({ theme }) => theme.colors.grey[400]};
    }
  }
`;

export const StyledTagContent = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.8rem;
  gap: 1rem;
  padding: 0.85rem 1.2rem;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.secondary.main};
  margin-bottom: 1.6rem;
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
  color: black;
`;

export const StyledTab = styled(Tab)`
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  margin-top: 2rem;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    margin: 1rem 0rem;
  }
`;

export const StyledContainer = styled(Container)`
  margin-top: 3rem;
  margin-bottom: 6rem;
  padding: 0;
  display: grid;

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    margin-bottom: 8rem;
  }
`;

export const StyledLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10rem;
  margin-bottom: 10rem;
`;

export const StyledLoader = styled(StyledSpinner)`
  width: 5rem;
  height: 5rem;
`;
